import { List, Map } from 'immutable';

import { isTruthy } from '../bool';
import { getSize } from '../iterable/getSize';
import { reduce } from '../iterable/reduce';
import { get } from '../iterable/get';

import { ALL, COMBINATIONS, DELETED, INFLUENCE, NUMBER_OF_VARIANTS, VARIANTS } from '../../constants/variations';
import { AMOUNT, ATTRIBUTE_DEFAULTS, CURRENCY, VALUE } from '../../constants/product';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '../../constants/channels';
import { ATTRIBUTE } from '../../constants/attributes';
import { EMPTY } from '../../constants';

function getCombinationPath(combination) {
  return combination.get('options').map(get('name')).toArray();
}

export function isCombinationDeleted({ combination, deleted }) {
  function reduceOptions(result, option) {
    if (result.included) return result;

    result.path.push(option.get('name'));
    result.included = deleted.getIn(result.path) === true;

    return result;
  }

  return combination.get('options').reduce(reduceOptions, { included: false, path: [] }).included;
}

export function isCombinationForbidden({ combination, influence }) {
  function reduceOptions(result, option, optionIndex) {
    if (!result && influence.hasIn([optionIndex, 'affectedBy'])) {
      const affectingVariantIndex = influence.getIn([optionIndex, 'affectedBy']);
      const affectingVariantOption = combination.getIn(['options', affectingVariantIndex, 'name']);

      return (
        influence.hasIn([affectingVariantIndex, 'values', affectingVariantOption]) &&
        !influence.hasIn([optionIndex, 'values', affectingVariantOption, option.get('name')])
      );
    }

    return result;
  }

  return combination.get('options').reduce(reduceOptions, false);
}

export function isOptionDeleted({ deleted, depth = 0, option, variants, variantIndex }) {
  if (depth === variantIndex) {
    if (deleted === true) {
      return true;
    }

    if (Map.isMap(deleted) && deleted.get(option.get('value')) === true) {
      return true;
    }

    return false;
  }

  function reduceDeleted(result, combination) {
    return (
      result &&
      isOptionDeleted({ deleted: combination, depth: depth + 1, option, variants, variantIndex })
    );
  }

  if (depth < variantIndex) {
    const options = variants.getIn([depth, 'options']);

    if (Map.isMap(deleted) && getSize(deleted) < getSize(options)) {
      return false;
    }

    return deleted.reduce(reduceDeleted, true);
  }

  return false;
}

export function isVariantDeleted({ deleted, depth = 0, variants }) {
  if (deleted === true) {
    return true;
  }

  if (!Map.isMap(deleted)) {
    return false;
  }

  const options = variants.getIn([depth, 'options']);

  if (getSize(deleted) < getSize(options)) {
    return false;
  }

  function reduceDeleted(result, combination) {
    return (
      isVariantDeleted({ deleted: combination, depth: depth + 1, variants }) &&
      result
    );
  }

  if (depth < getSize(variants)) {
    return deleted.reduce(reduceDeleted, true);
  }

  return false;
}

function addCombinationToDeleted({ combination, deleted }) {
  const path = getCombinationPath(combination);
  return deleted.setIn(path, true);
}

function removeCombinationFromDeleted({ combination, deleted: source }) {
  const path = getCombinationPath(combination);
  let deleted = source.deleteIn(path);

  path.pop();

  while (getSize(path) > 0) {
    if (getSize(deleted.getIn(path)) === 0) {
      deleted = deleted.deleteIn(path);
    }

    path.pop();
  }

  return deleted;
}

export function getDeletedCombinations({ variations }) {
  const variants = variations.get(VARIANTS);
  const combinations = variations.get(COMBINATIONS);

  if (getSize(variants) === 0) return EMPTY.MAP;

  function getCombinationsMap(depth) {
    function reduceOptions(result, option) {
      const combination = depth === getSize(variants) - 1
        ? true
        : getCombinationsMap(depth + 1);

      return result.set(option.get('value'), combination);
    }

    return variants.getIn([depth, 'options']).reduce(reduceOptions, EMPTY.MAP);
  }

  function reduceCombinations(deleted, combination) {
    if (isCombinationDeleted({ combination, deleted })) {
      if (!combination.get(DELETED)) {
        return removeCombinationFromDeleted({ combination, deleted });
      }
    } else if (combination.get(DELETED)) {
      return addCombinationToDeleted({ combination, deleted });
    }

    return deleted;
  }

  function reduceDeleted(deleted, depth) {
    if (!Map.isMap(deleted) || getSize(deleted) === 0) {
      return deleted;
    }

    if (depth === getSize(variants) && getSize(deleted) === getSize(variants.getIn([depth, 'options']))) {
      return true;
    }

    function mapDeleted(items) {
      return reduceDeleted(items, depth + 1);
    }

    return deleted.map(mapDeleted);
  }

  return reduceDeleted(combinations.reduce(reduceCombinations, getCombinationsMap(0)), 0);
}

export function getGenericCombination({ channel, combinations, influence, inventory, shipping, variants }) {
  switch (channel) {
    case EBAY: {
      const genericCombination = {};

      if (inventory) {
        genericCombination[ATTRIBUTE.PRICE] = (
          inventory.getIn([ATTRIBUTE.PRICE, VALUE]) ||
          ATTRIBUTE_DEFAULTS[EBAY][ATTRIBUTE.PRICE]
        );

        genericCombination[ATTRIBUTE.QUANTITY] = (
          (
            getSize(variants) === 1 &&
            getSize(variants.getIn([0, 'options'])) === 1 &&
            inventory.getIn([ATTRIBUTE.QUANTITY, VALUE])
          ) ||
          ATTRIBUTE_DEFAULTS[EBAY][ATTRIBUTE.QUANTITY]
        );

        genericCombination[ATTRIBUTE.SKU] = getSize(combinations) > 0
          ? ATTRIBUTE_DEFAULTS[EBAY][ATTRIBUTE.SKU]
          : inventory.getIn([ATTRIBUTE.SKU, VALUE]) || ATTRIBUTE_DEFAULTS[EBAY][ATTRIBUTE.SKU];
      } else {
        genericCombination[ATTRIBUTE.PRICE] = null;
        genericCombination[ATTRIBUTE.QUANTITY] = null;
        genericCombination[ATTRIBUTE.SKU] = null;
      }

      return genericCombination;
    }

    case ETSY: {
      const genericCombination = {
        [ATTRIBUTE.VISIBILITY]: true,
      };

      if (inventory) {
        genericCombination[ATTRIBUTE.PRICE] = getSize(influence.get(ATTRIBUTE.PRICE)) > 0
          ? ATTRIBUTE_DEFAULTS[ETSY][ATTRIBUTE.PRICE]
          : inventory.getIn([ATTRIBUTE.PRICE, VALUE]);

        genericCombination[ATTRIBUTE.QUANTITY] = getSize(influence.get(ATTRIBUTE.QUANTITY)) > 0
          ? ATTRIBUTE_DEFAULTS[ETSY][ATTRIBUTE.QUANTITY]
          : inventory.getIn([ATTRIBUTE.QUANTITY, VALUE]);

        genericCombination[ATTRIBUTE.SKU] = getSize(influence.get(ATTRIBUTE.SKU)) > 0
          ? ATTRIBUTE_DEFAULTS[ETSY][ATTRIBUTE.SKU]
          : inventory.getIn([ATTRIBUTE.SKU, VALUE]);
      } else {
        genericCombination[ATTRIBUTE.PRICE] = null;
        genericCombination[ATTRIBUTE.QUANTITY] = null;
        genericCombination[ATTRIBUTE.SKU] = null;
      }

      if (shipping) {
        genericCombination[ATTRIBUTE.PROCESSING_PROFILE] = getSize(influence.get(ATTRIBUTE.PROCESSING_PROFILE)) > 0
          ? ATTRIBUTE_DEFAULTS[ETSY][ATTRIBUTE.PROCESSING_PROFILE]
          : shipping.getIn([ATTRIBUTE.PROCESSING_PROFILE, VALUE]);
      } else {
        genericCombination[ATTRIBUTE.PROCESSING_PROFILE] = null;
      }

      return genericCombination;
    }

    case FAIRE: {
      const genericCombination = {
        imageUrl: null,
      };

      if (inventory) {
        genericCombination[ATTRIBUTE.DEADLINE_DATE] = inventory.getIn([ATTRIBUTE.PREORDER, VALUE])
          ? inventory.getIn([ATTRIBUTE.DEADLINE_DATE, VALUE]) || ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.DEADLINE_DATE]
          : ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.DEADLINE_DATE];

        genericCombination[ATTRIBUTE.END_DATE] = inventory.getIn([ATTRIBUTE.PREORDER, VALUE])
          ? inventory.getIn([ATTRIBUTE.END_DATE, VALUE]) || ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.END_DATE]
          : ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.END_DATE];

        genericCombination[ATTRIBUTE.GTIN] = (
          inventory.getIn([ATTRIBUTE.GTIN, VALUE]) ||
          ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.GTIN]
        );

        genericCombination[ATTRIBUTE.INTERNATIONAL_PRICING] = inventory.hasIn([ATTRIBUTE.INTERNATIONAL_PRICING, VALUE])
          ? inventory.getIn([ATTRIBUTE.INTERNATIONAL_PRICING, VALUE])
          : ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.INTERNATIONAL_PRICING];

        genericCombination[ATTRIBUTE.PREORDER] = inventory.hasIn([ATTRIBUTE.PREORDER, VALUE])
          ? inventory.getIn([ATTRIBUTE.PREORDER, VALUE])
          : ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.PREORDER];

        genericCombination[ATTRIBUTE.QUANTITY] = (
          (
            getSize(variants) === 1 &&
            getSize(variants.getIn([0, 'options'])) === 1 &&
            inventory.getIn([ATTRIBUTE.QUANTITY, VALUE])
          ) ||
          ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.QUANTITY]
        );

        genericCombination[ATTRIBUTE.RESTOCK_DATE] = genericCombination[ATTRIBUTE.QUANTITY] === AMOUNT.ZERO
          ? (
            inventory.getIn([ATTRIBUTE.RESTOCK_DATE, VALUE]) ||
            ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.RESTOCK_DATE]
          )
          : ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.RESTOCK_DATE];

        genericCombination[ATTRIBUTE.SKU] = getSize(combinations) > 0
          ? ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.SKU]
          : inventory.getIn([ATTRIBUTE.SKU, VALUE]) || ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.SKU];

        genericCombination[ATTRIBUTE.START_DATE] = inventory.getIn([ATTRIBUTE.PREORDER, VALUE])
          ? inventory.getIn([ATTRIBUTE.START_DATE, VALUE]) || ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.START_DATE]
          : ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.START_DATE];

        genericCombination[ATTRIBUTE.TARIFF_CODE] = (
          inventory.getIn([ATTRIBUTE.TARIFF_CODE, VALUE]) ||
          ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.TARIFF_CODE]
        );

        genericCombination[ATTRIBUTE.RETAIL_PRICE] = Map({
          [CURRENCY.AUD]: (
            inventory.getIn([ATTRIBUTE.RETAIL_PRICE, VALUE, CURRENCY.AUD]) ||
            ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.RETAIL_PRICE]
          ),
          [CURRENCY.CAD]: (
            inventory.getIn([ATTRIBUTE.RETAIL_PRICE, VALUE, CURRENCY.CAD]) ||
            ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.RETAIL_PRICE]
          ),
          [CURRENCY.EUR]: (
            inventory.getIn([ATTRIBUTE.RETAIL_PRICE, VALUE, CURRENCY.EUR]) ||
            ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.RETAIL_PRICE]
          ),
          [CURRENCY.GBP]: (
            inventory.getIn([ATTRIBUTE.RETAIL_PRICE, VALUE, CURRENCY.GBP]) ||
            ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.RETAIL_PRICE]
          ),
          [CURRENCY.USD]: (
            inventory.getIn([ATTRIBUTE.RETAIL_PRICE, VALUE, CURRENCY.USD]) ||
            ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.RETAIL_PRICE]
          ),
        });

        genericCombination[ATTRIBUTE.WHOLESALE_PRICE] = Map({
          [CURRENCY.AUD]: (
            inventory.getIn([ATTRIBUTE.WHOLESALE_PRICE, VALUE, CURRENCY.AUD]) ||
            ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.WHOLESALE_PRICE]
          ),
          [CURRENCY.CAD]: (
            inventory.getIn([ATTRIBUTE.WHOLESALE_PRICE, VALUE, CURRENCY.CAD]) ||
            ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.WHOLESALE_PRICE]
          ),
          [CURRENCY.EUR]: (
            inventory.getIn([ATTRIBUTE.WHOLESALE_PRICE, VALUE, CURRENCY.EUR]) ||
            ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.WHOLESALE_PRICE]
          ),
          [CURRENCY.GBP]: (
            inventory.getIn([ATTRIBUTE.WHOLESALE_PRICE, VALUE, CURRENCY.GBP]) ||
            ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.WHOLESALE_PRICE]
          ),
          [CURRENCY.USD]: (
            inventory.getIn([ATTRIBUTE.WHOLESALE_PRICE, VALUE, CURRENCY.USD]) ||
            ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.WHOLESALE_PRICE]
          ),
        });

        genericCombination[ATTRIBUTE.MASS_UNIT] = (
          inventory.getIn([ATTRIBUTE.MASS_UNIT, VALUE]) ||
          ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.MASS_UNIT]
        );

        genericCombination[ATTRIBUTE.WEIGHT] = (
          inventory.getIn([ATTRIBUTE.WEIGHT, VALUE]) ||
          ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.WEIGHT]
        );

        genericCombination[ATTRIBUTE.DISTANCE_UNIT] = (
          inventory.getIn([ATTRIBUTE.DISTANCE_UNIT, VALUE]) ||
          ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.DISTANCE_UNIT]
        );

        genericCombination[ATTRIBUTE.LENGTH] = (
          inventory.getIn([ATTRIBUTE.LENGTH, VALUE]) ||
          ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.LENGTH]
        );

        genericCombination[ATTRIBUTE.WIDTH] = (
          inventory.getIn([ATTRIBUTE.WIDTH, VALUE]) ||
          ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.WIDTH]
        );

        genericCombination[ATTRIBUTE.HEIGHT] = (
          inventory.getIn([ATTRIBUTE.HEIGHT, VALUE]) ||
          ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.HEIGHT]
        );
      } else {
        genericCombination[ATTRIBUTE.DEADLINE_DATE] = null;
        genericCombination[ATTRIBUTE.END_DATE] = null;
        genericCombination[ATTRIBUTE.GTIN] = null;
        genericCombination[ATTRIBUTE.INTERNATIONAL_PRICING] = false;
        genericCombination[ATTRIBUTE.PREORDER] = false;
        genericCombination[ATTRIBUTE.QUANTITY] = null;
        genericCombination[ATTRIBUTE.RESTOCK_DATE] = null;
        genericCombination[ATTRIBUTE.SKU] = null;
        genericCombination[ATTRIBUTE.START_DATE] = null;
        genericCombination[ATTRIBUTE.TARIFF_CODE] = null;
        genericCombination[ATTRIBUTE.RETAIL_PRICE] = Map({
          [CURRENCY.AUD]: null,
          [CURRENCY.CAD]: null,
          [CURRENCY.EUR]: null,
          [CURRENCY.GBP]: null,
          [CURRENCY.USD]: null,
        });

        genericCombination[ATTRIBUTE.WHOLESALE_PRICE] = Map({
          [CURRENCY.AUD]: null,
          [CURRENCY.CAD]: null,
          [CURRENCY.EUR]: null,
          [CURRENCY.GBP]: null,
          [CURRENCY.USD]: null,
        });

        genericCombination[ATTRIBUTE.MASS_UNIT] = ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.MASS_UNIT];
        genericCombination[ATTRIBUTE.WEIGHT] = null;
        genericCombination[ATTRIBUTE.DISTANCE_UNIT] = ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.DISTANCE_UNIT];
        genericCombination[ATTRIBUTE.LENGTH] = null;
        genericCombination[ATTRIBUTE.WIDTH] = null;
        genericCombination[ATTRIBUTE.HEIGHT] = null;
      }

      return genericCombination;
    }

    case SHOPIFY: {
      const genericCombination = {
        imageId: null,
        imageHash: null,
      };

      if (inventory) {
        genericCombination[ATTRIBUTE.BARCODE] = (
          inventory.getIn([ATTRIBUTE.BARCODE, VALUE]) ||
          ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.BARCODE]
        );

        genericCombination[ATTRIBUTE.CAP] = (
          inventory.getIn([ATTRIBUTE.CAP, VALUE]) ||
          ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.CAP]
        );

        genericCombination[ATTRIBUTE.CHARGE_TAX] = inventory.hasIn([ATTRIBUTE.CHARGE_TAX, VALUE])
          ? inventory.getIn([ATTRIBUTE.CHARGE_TAX, VALUE])
          : ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.CHARGE_TAX];

        genericCombination[ATTRIBUTE.CONTINUE_SELLING] = inventory.hasIn([ATTRIBUTE.CONTINUE_SELLING, VALUE])
          ? inventory.getIn([ATTRIBUTE.CONTINUE_SELLING, VALUE])
          : ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.CONTINUE_SELLING];

        genericCombination[ATTRIBUTE.COUNTRY_CODE] = (
          inventory.getIn([ATTRIBUTE.COUNTRY_CODE, VALUE]) ||
          ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.COUNTRY_CODE]
        );

        genericCombination[ATTRIBUTE.CPI] = (
          inventory.getIn([ATTRIBUTE.CPI, VALUE]) ||
          ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.CPI]
        );

        genericCombination[ATTRIBUTE.HS_CODE] = (
          inventory.getIn([ATTRIBUTE.HS_CODE, VALUE]) ||
          ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.HS_CODE]
        );

        genericCombination[ATTRIBUTE.PHYSICAL] = inventory.hasIn([ATTRIBUTE.PHYSICAL, VALUE])
          ? inventory.getIn([ATTRIBUTE.PHYSICAL, VALUE])
          : ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.PHYSICAL];

        genericCombination[ATTRIBUTE.PRICE] = (
          inventory.getIn([ATTRIBUTE.PRICE, VALUE]) ||
          ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.PRICE]
        );

        genericCombination[ATTRIBUTE.QUANTITY] = (
          (
            getSize(variants) === 1 &&
            getSize(variants.getIn([0, 'options'])) === 1 &&
            inventory.getIn([ATTRIBUTE.QUANTITY, VALUE])
          ) ||
          ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.QUANTITY]
        );

        genericCombination[ATTRIBUTE.SKU] = getSize(combinations)
          ? ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.SKU]
          : inventory.getIn([ATTRIBUTE.SKU, VALUE]) || ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.SKU];

        genericCombination[ATTRIBUTE.TRACK_QUANTITY] = inventory.hasIn([ATTRIBUTE.CHARGE_TAX, VALUE])
          ? inventory.getIn([ATTRIBUTE.TRACK_QUANTITY, VALUE])
          : ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.TRACK_QUANTITY];

        genericCombination[ATTRIBUTE.UNIT] = (
          inventory.getIn([ATTRIBUTE.UNIT, VALUE]) ||
          ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.UNIT]
        );

        genericCombination[ATTRIBUTE.WEIGHT] = (
          inventory.getIn([ATTRIBUTE.WEIGHT, VALUE]) ||
          ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.WEIGHT]
        );
      } else {
        genericCombination[ATTRIBUTE.BARCODE] = null;
        genericCombination[ATTRIBUTE.CAP] = null;
        genericCombination[ATTRIBUTE.CHARGE_TAX] = true;
        genericCombination[ATTRIBUTE.CONTINUE_SELLING] = null;
        genericCombination[ATTRIBUTE.COUNTRY_CODE] = null;
        genericCombination[ATTRIBUTE.CPI] = null;
        genericCombination[ATTRIBUTE.HS_CODE] = null;
        genericCombination[ATTRIBUTE.PHYSICAL] = true;
        genericCombination[ATTRIBUTE.PRICE] = null;
        genericCombination[ATTRIBUTE.QUANTITY] = null;
        genericCombination[ATTRIBUTE.SKU] = null;
        genericCombination[ATTRIBUTE.TRACK_QUANTITY] = false;
        genericCombination[ATTRIBUTE.UNIT] = ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.UNIT];
        genericCombination[ATTRIBUTE.WEIGHT] = null;
      }

      return genericCombination;
    }

    default: {
      return {};
    }
  }
}

export function findExistingCombination({ channel, combinations, config }) {
  function filterCombinations(combination) {
    function reduceProperties(all, property) {
      function findOption(option) {
        function reduceProperty(result, value, key) {
          return result && option.get(key) === value;
        }

        return reduce(property, true, reduceProperty);
      }

      return all && combination.get('options').find(findOption);
    }

    return config.options.reduce(reduceProperties, true);
  }

  function reduceVisible(visible, offering) {
    return visible || offering.get(ATTRIBUTE.VISIBILITY);
  }

  function findSameCombination(combination) {
    const options = combination.get('options');

    if (getSize(config.names) > getSize(options)) {
      config.names.splice(getSize(options) - getSize(config.names));
    }

    function reduceNames(result, name, index) {
      return result && (name ? options.getIn([index, 'name']) === name : result);
    }

    return config.names.reduce(reduceNames, true);
  }

  switch (channel) {
    case EBAY:
    case FAIRE:
    case SHOPIFY: {
      return combinations.find(findSameCombination);
    }

    case ETSY: {
      const allSuitableCombinations = combinations.filter(filterCombinations);

      return allSuitableCombinations.has(0)
        ? allSuitableCombinations
          .get(0)
          .set(ATTRIBUTE.VISIBILITY, allSuitableCombinations.reduce(reduceVisible, false))
        : undefined;
    }

    default: {
      return undefined;
    }
  }
}

export function findSuitableCombination({ combinations, optionId, variantId }) {
  function findCombination(combination) {
    function findOption(option) {
      return option.get('variantId') === variantId && option.get('optionId') === optionId;
    }

    return combination.get('options').find(findOption);
  }

  return combinations.find(findCombination);
}

export function getCombination({ channel, combinations, config, genericCombination }) {
  switch (channel) {
    case EBAY: {
      const filteredNames = config.names.filter(isTruthy);
      const existing = findExistingCombination({ channel, combinations, config });

      if (!existing) return genericCombination;

      return {
        [ATTRIBUTE.PRICE]: existing.get(ATTRIBUTE.PRICE),
        [ATTRIBUTE.QUANTITY]: existing.get(ATTRIBUTE.QUANTITY),
        [ATTRIBUTE.SKU]: getSize(filteredNames) > getSize(existing.get('options'))
          ? genericCombination[ATTRIBUTE.SKU]
          : existing.get(ATTRIBUTE.SKU),
      };
    }

    case ETSY: {
      const existing = findExistingCombination({ channel, combinations, config });

      if (!existing) return genericCombination;

      return {
        [ATTRIBUTE.PRICE]: config.influence[ATTRIBUTE.PRICE]
          ? existing.get(ATTRIBUTE.PRICE)
          : genericCombination[ATTRIBUTE.PRICE],
        [ATTRIBUTE.PROCESSING_PROFILE]: config.influence[ATTRIBUTE.PROCESSING_PROFILE]
          ? existing.get(ATTRIBUTE.PROCESSING_PROFILE)
          : genericCombination[ATTRIBUTE.PROCESSING_PROFILE],
        [ATTRIBUTE.QUANTITY]: config.influence[ATTRIBUTE.QUANTITY]
          ? existing.get(ATTRIBUTE.QUANTITY)
          : genericCombination[ATTRIBUTE.QUANTITY],
        [ATTRIBUTE.SKU]: config.influence[ATTRIBUTE.SKU]
          ? existing.get(ATTRIBUTE.SKU)
          : genericCombination[ATTRIBUTE.SKU],
        [ATTRIBUTE.VISIBILITY]: existing.get(ATTRIBUTE.VISIBILITY),
      };
    }

    case FAIRE: {
      const filteredNames = config.names.filter(isTruthy);
      const existing = findExistingCombination({ channel, combinations, config });

      if (!existing) return genericCombination;

      const combination = {
        imageUrl: existing.get('imageUrl'),
        [ATTRIBUTE.DEADLINE_DATE]: existing.get(ATTRIBUTE.PREORDER)
          ? existing.get(ATTRIBUTE.DEADLINE_DATE)
          : genericCombination[ATTRIBUTE.DEADLINE_DATE],
        [ATTRIBUTE.END_DATE]: existing.get(ATTRIBUTE.PREORDER)
          ? existing.get(ATTRIBUTE.END_DATE)
          : genericCombination[ATTRIBUTE.END_DATE],
        [ATTRIBUTE.GTIN]: existing.get(ATTRIBUTE.GTIN),
        [ATTRIBUTE.HEIGHT]: existing.get(ATTRIBUTE.HEIGHT),
        [ATTRIBUTE.INTERNATIONAL_PRICING]: (
          !!existing.getIn([ATTRIBUTE.RETAIL_PRICE, CURRENCY.AUD]) ||
          !!existing.getIn([ATTRIBUTE.RETAIL_PRICE, CURRENCY.CAD]) ||
          !!existing.getIn([ATTRIBUTE.RETAIL_PRICE, CURRENCY.EUR]) ||
          !!existing.getIn([ATTRIBUTE.RETAIL_PRICE, CURRENCY.GBP]) ||
          !!existing.getIn([ATTRIBUTE.WHOLESALE_PRICE, CURRENCY.AUD]) ||
          !!existing.getIn([ATTRIBUTE.WHOLESALE_PRICE, CURRENCY.CAD]) ||
          !!existing.getIn([ATTRIBUTE.WHOLESALE_PRICE, CURRENCY.EUR]) ||
          !!existing.getIn([ATTRIBUTE.WHOLESALE_PRICE, CURRENCY.GBP])
        ),
        [ATTRIBUTE.LENGTH]: existing.get(ATTRIBUTE.LENGTH),
        [ATTRIBUTE.PREORDER]: existing.get(ATTRIBUTE.PREORDER),
        [ATTRIBUTE.QUANTITY]: existing.get(ATTRIBUTE.QUANTITY),
        [ATTRIBUTE.RESTOCK_DATE]: existing.get(ATTRIBUTE.RESTOCK_DATE),
        [ATTRIBUTE.RETAIL_PRICE]: existing.get(ATTRIBUTE.RETAIL_PRICE),
        [ATTRIBUTE.SKU]: getSize(filteredNames) > getSize(existing.get('options'))
          ? genericCombination[ATTRIBUTE.SKU]
          : existing.get(ATTRIBUTE.SKU),
        [ATTRIBUTE.START_DATE]: existing.get(ATTRIBUTE.PREORDER)
          ? existing.get(ATTRIBUTE.START_DATE)
          : genericCombination[ATTRIBUTE.START_DATE],
        [ATTRIBUTE.TARIFF_CODE]: existing.get(ATTRIBUTE.TARIFF_CODE),
        [ATTRIBUTE.WEIGHT]: existing.get(ATTRIBUTE.WEIGHT),
        [ATTRIBUTE.WHOLESALE_PRICE]: existing.get(ATTRIBUTE.WHOLESALE_PRICE),
        [ATTRIBUTE.WIDTH]: existing.get(ATTRIBUTE.WIDTH),
      };

      return combination;
    }

    case SHOPIFY: {
      const filteredNames = config.names.filter(isTruthy);
      const existing = findExistingCombination({ channel, combinations, config });

      if (!existing) return genericCombination;

      return {
        imageHash: existing.get('imageHash'),
        imageId: existing.get('imageId'),
        [DELETED]: existing.get(DELETED),
        [ATTRIBUTE.BARCODE]: existing.get(ATTRIBUTE.BARCODE),
        [ATTRIBUTE.CAP]: existing.get(ATTRIBUTE.CAP),
        [ATTRIBUTE.CHARGE_TAX]: existing.get(ATTRIBUTE.CHARGE_TAX),
        [ATTRIBUTE.CONTINUE_SELLING]: existing.get(ATTRIBUTE.CONTINUE_SELLING),
        [ATTRIBUTE.COUNTRY_CODE]: existing.get(ATTRIBUTE.COUNTRY_CODE),
        [ATTRIBUTE.CPI]: existing.get(ATTRIBUTE.CPI),
        [ATTRIBUTE.HS_CODE]: existing.get(ATTRIBUTE.HS_CODE),
        [ATTRIBUTE.PHYSICAL]: existing.get(ATTRIBUTE.PHYSICAL),
        [ATTRIBUTE.PRICE]: existing.get(ATTRIBUTE.PRICE),
        [ATTRIBUTE.QUANTITY]: existing.get(ATTRIBUTE.TRACK_QUANTITY)
          ? existing.get(ATTRIBUTE.QUANTITY)
          : genericCombination[ATTRIBUTE.QUANTITY],
        [ATTRIBUTE.SKU]: getSize(filteredNames) > getSize(existing.get('options'))
          ? genericCombination[ATTRIBUTE.SKU]
          : existing.get(ATTRIBUTE.SKU),
        [ATTRIBUTE.TRACK_QUANTITY]: existing.get(ATTRIBUTE.TRACK_QUANTITY),
        [ATTRIBUTE.UNIT]: existing.get(ATTRIBUTE.UNIT),
        [ATTRIBUTE.WEIGHT]: existing.get(ATTRIBUTE.WEIGHT),
      };
    }

    default: {
      return {};
    }
  }
}

function updateEbayCombinations({
  channel,
  combinations = EMPTY.LIST,
  genericCombination,
  influence,
  options = EMPTY.LIST,
  variants,
  variantIndex = 0,
  variations,
}) {
  let result = combinations;

  function mapOptions(option) {
    return Map({ name: option.get('name') });
  }

  function reduceNames(names, option) {
    function findVariant(variant) {
      return variant.get('id') === option.get('variantId');
    }

    const index = variations.get(VARIANTS).findIndex(findVariant);

    if (index !== -1) {
      names[index] = option.get('name');
    }

    return names;
  }

  for (let optionIndex = 0; optionIndex < getSize(variants.getIn([variantIndex, 'options'])); optionIndex += 1) {
    const option = variants.getIn([variantIndex, 'options', optionIndex]);
    const name = option.get('value');
    const variantId = variants.getIn([variantIndex, 'id']);
    const newOptions = options.push(Map({ name, variantId }));

    if (isCombinationForbidden({ combination: Map({ options: newOptions }), influence })) {
      continue;
    }

    if (
      variantIndex === NUMBER_OF_VARIANTS[channel] - 1 ||
      getSize(variants.getIn([variantIndex + 1, 'options'])) === 0
    ) {
      const config = { names: newOptions.reduce(reduceNames, Array(getSize(newOptions) + 1).fill(undefined)) };
      const combination = getCombination({
        channel,
        combinations: variations.get(COMBINATIONS),
        config,
        genericCombination,
      });

      result = result.push(Map({ id: -getSize(result) - 1, options: newOptions.map(mapOptions), ...combination }));
    } else {
      result = updateEbayCombinations({
        channel,
        combinations: result,
        influence,
        genericCombination,
        options: newOptions,
        variants,
        variantIndex: variantIndex + 1,
        variations,
      });
    }
  }

  return result;
}

function updateEtsyCombinations({ channel, genericCombination, influence, variants, variations }) {
  let id = 0;
  let result = EMPTY.LIST;
  const combinations = variations.get(COMBINATIONS);

  for (const option1 of variants.getIn([0, 'options'])) {
    const name1 = option1.get('value');
    const option1Id = option1.get('id');
    const variant1Id = variants.getIn([0, 'id']);
    const config = {
      influence: {
        [ATTRIBUTE.PRICE]: influence.getIn([ATTRIBUTE.PRICE, 0]),
        [ATTRIBUTE.PROCESSING_PROFILE]: influence.getIn([ATTRIBUTE.PROCESSING_PROFILE, 0]),
        [ATTRIBUTE.QUANTITY]: influence.getIn([ATTRIBUTE.QUANTITY, 0]),
        [ATTRIBUTE.SKU]: influence.getIn([ATTRIBUTE.SKU, 0]),
      },
      options: [{ optionId: option1Id, variantId: variant1Id }],
    };

    if (getSize(variants.getIn([1, 'options'])) === 0) {
      id -= 1;

      const options = List([
        Map({ optionId: option1Id, variantId: variant1Id, name: name1 }),
      ]);

      result = result.push(
        Map({
          id,
          options,
          ...getCombination({ channel, combinations, config, genericCombination }),
        })
      );
    } else {
      for (const option2 of variants.getIn([1, 'options'])) {
        id -= 1;
        const name2 = option2.get('value');
        const option2Id = option2.get('id');
        const variant2Id = variants.getIn([1, 'id']);
        const options = List([
          Map({ optionId: option1Id, variantId: variant1Id, name: name1 }),
          Map({ optionId: option2Id, variantId: variant2Id, name: name2 }),
        ]);

        if (getSize(combinations.getIn([0, 'options'])) < getSize(variants)) {
          result = result.push(
            Map({
              id,
              options,
              ...getCombination({ channel, combinations, config, genericCombination }),
            })
          );
        } else {
          let existing = findExistingCombination({
            channel,
            combinations,
            config: {
              options: [
                { optionId: option1Id, variantId: variant1Id },
                { optionId: option2Id, variantId: variant2Id },
              ],
            },
          });

          if (existing) {
            const existingId = existing.get('id');

            if (Number.isFinite(existingId) && existingId < 0) {
              existing = existing.set('id', id);
            }

            result = result.push(existing);
          } else {
            if (influence.get(ALL)) {
              result = result.push(Map({ id, options, ...genericCombination }));
            } else {
              const { [ATTRIBUTE.VISIBILITY]: visibility } = genericCombination;
              let {
                [ATTRIBUTE.PRICE]: price,
                [ATTRIBUTE.PROCESSING_PROFILE]: processingProfile,
                [ATTRIBUTE.QUANTITY]: quantity,
                [ATTRIBUTE.SKU]: sku,
              } = genericCombination;

              if (getSize(influence.get(ATTRIBUTE.PRICE)) > 0) {
                const { optionId, variantId } = influence.getIn([ATTRIBUTE.PRICE, 0])
                  ? { optionId: option1Id, variantId: variant1Id }
                  : { optionId: option2Id, variantId: variant2Id };

                const suitableCombination = findSuitableCombination({
                  combinations,
                  optionId,
                  variantId,
                });

                price = suitableCombination
                  ? suitableCombination.get(ATTRIBUTE.PRICE)
                  : genericCombination[ATTRIBUTE.PRICE];
              }

              if (getSize(influence.get(ATTRIBUTE.PROCESSING_PROFILE)) > 0) {
                const { optionId, variantId } = influence.getIn([ATTRIBUTE.PROCESSING_PROFILE, 0])
                  ? { optionId: option1Id, variantId: variant1Id }
                  : { optionId: option2Id, variantId: variant2Id };

                const suitableCombination = findSuitableCombination({
                  combinations,
                  optionId,
                  variantId,
                });

                processingProfile = suitableCombination
                  ? suitableCombination.get(ATTRIBUTE.PROCESSING_PROFILE)
                  : genericCombination[ATTRIBUTE.PROCESSING_PROFILE];
              }

              if (getSize(influence.get(ATTRIBUTE.QUANTITY)) > 0) {
                const { optionId, variantId } = influence.getIn([ATTRIBUTE.QUANTITY, 0])
                  ? { optionId: option1Id, variantId: variant1Id }
                  : { optionId: option2Id, variantId: variant2Id };

                const suitableCombination = findSuitableCombination({
                  combinations,
                  optionId,
                  variantId,
                });

                quantity = suitableCombination
                  ? suitableCombination.get(ATTRIBUTE.QUANTITY)
                  : genericCombination[ATTRIBUTE.QUANTITY];
              }

              if (getSize(influence.get(ATTRIBUTE.SKU)) > 0) {
                const { optionId, variantId } = influence.getIn([ATTRIBUTE.SKU, 0])
                  ? { optionId: option1Id, variantId: variant1Id }
                  : { optionId: option2Id, variantId: variant2Id };

                const suitableCombination = findSuitableCombination({
                  combinations,
                  optionId,
                  variantId,
                });

                sku = suitableCombination
                  ? suitableCombination.get(ATTRIBUTE.SKU)
                  : genericCombination[ATTRIBUTE.SKU];
              }

              result = result.push(
                Map({
                  id,
                  options,
                  [ATTRIBUTE.PRICE]: price,
                  [ATTRIBUTE.PROCESSING_PROFILE]: processingProfile,
                  [ATTRIBUTE.QUANTITY]: quantity,
                  [ATTRIBUTE.SKU]: sku,
                  [ATTRIBUTE.VISIBILITY]: visibility,
                })
              );
            }
          }
        }
      }
    }
  }

  return result;
}

function updateFaireCombinations({
  channel,
  combinations = EMPTY.LIST,
  genericCombination,
  options = EMPTY.LIST,
  variants,
  variantIndex = 0,
  variations,
}) {
  let result = combinations;

  function mapOptions(option) {
    return Map({ name: option.get('name') });
  }

  function reduceNames(names, option) {
    function findVariant(variant) {
      return variant.get('id') === option.get('variantId');
    }

    const index = variations.get(VARIANTS).findIndex(findVariant);

    if (index !== -1) {
      names[index] = option.get('name');
    }

    return names;
  }

  for (let optionIndex = 0; optionIndex < getSize(variants.getIn([variantIndex, 'options'])); optionIndex += 1) {
    const option = variants.getIn([variantIndex, 'options', optionIndex]);
    const name = option.get('value');
    const variantId = variants.getIn([variantIndex, 'id']);
    const newOptions = options.push(Map({ name, variantId }));

    if (
      variantIndex === NUMBER_OF_VARIANTS[channel] - 1 ||
      getSize(variants.getIn([variantIndex + 1, 'options'])) === 0
    ) {
      const combination = getCombination({
        channel,
        combinations: variations.get(COMBINATIONS),
        config: { names: newOptions.reduce(reduceNames, Array(getSize(newOptions) + 1).fill(undefined)) },
        genericCombination,
      });

      result = result.push(Map({ id: -getSize(result) - 1, options: newOptions.map(mapOptions), ...combination }));
    } else {
      result = updateFaireCombinations({
        channel,
        combinations: result,
        genericCombination,
        options: newOptions,
        variants,
        variantIndex: variantIndex + 1,
        variations,
      });
    }
  }

  return result;
}

function updateShopifyCombinations({
  channel,
  combinations = EMPTY.LIST,
  deleted,
  genericCombination,
  options = EMPTY.LIST,
  variants,
  variantIndex = 0,
  variations,
}) {
  let result = combinations;

  function mapOptions(option) {
    return Map({ name: option.get('name') });
  }

  function reduceNames(names, option) {
    function findVariant(variant) {
      return variant.get('id') === option.get('variantId');
    }

    const index = variations.get(VARIANTS).findIndex(findVariant);

    if (index !== -1) {
      names[index] = option.get('name');
    }

    return names;
  }

  for (let optionIndex = 0; optionIndex < getSize(variants.getIn([variantIndex, 'options'])); optionIndex += 1) {
    const option = variants.getIn([variantIndex, 'options', optionIndex]);
    const name = option.get('value');
    const variantId = variants.getIn([variantIndex, 'id']);
    const newOptions = options.push(Map({ name, variantId }));

    if (
      variantIndex === NUMBER_OF_VARIANTS[channel] - 1 ||
      getSize(variants.getIn([variantIndex + 1, 'options'])) === 0
    ) {
      const combination = getCombination({
        channel,
        combinations: variations.get(COMBINATIONS),
        config: { names: newOptions.reduce(reduceNames, Array(getSize(newOptions) + 1).fill(undefined)) },
        genericCombination,
      });

      if (
        combination === genericCombination &&
        isCombinationDeleted({ combination: Map({ options: newOptions }), deleted })
      ) {
        continue;
      }

      result = result.push(Map({ id: -getSize(result) - 1, options: newOptions.map(mapOptions), ...combination }));
    } else {
      result = updateShopifyCombinations({
        channel,
        combinations: result,
        deleted,
        genericCombination,
        options: newOptions,
        variants,
        variantIndex: variantIndex + 1,
        variations,
      });
    }
  }

  return result;
}

export function updateCombinations({ channel, inventory, shipping, variants, variations }) {
  if (getSize(variants) === 0) return EMPTY.LIST;

  switch (channel) {
    case EBAY: {
      const combinations = variations.get(COMBINATIONS);
      const influence = variations.get(INFLUENCE);
      const genericCombination = getGenericCombination({ channel, combinations, inventory, variants });
      return updateEbayCombinations({ channel, genericCombination, influence, variants, variations });
    }

    case ETSY: {
      const influence = variations.get(INFLUENCE);
      const genericCombination = getGenericCombination({ channel, influence, inventory, shipping });
      return updateEtsyCombinations({ channel, genericCombination, influence, variants, variations });
    }

    case FAIRE: {
      const combinations = variations.get(COMBINATIONS);
      const genericCombination = getGenericCombination({ channel, combinations, inventory, variants });
      return updateFaireCombinations({ channel, genericCombination, variants, variations });
    }

    case SHOPIFY: {
      const combinations = variations.get(COMBINATIONS);
      const deleted = variations.get(DELETED, EMPTY.MAP);
      const genericCombination = getGenericCombination({ channel, combinations, inventory, variants });
      return updateShopifyCombinations({ channel, deleted, genericCombination, variants, variations });
    }

    default: {
      return EMPTY.LIST;
    }
  }
}

export function getOptionsFromCombinations({ combinations, variantIndex }) {
  function reduceCombinationsToOptionsAndIndexes(result, combination, combinationIndex) {
    function findSameVariantCombination(item) {
      return item.getIn(['options', 0, 'name']) === options.getIn([variantIndex, 'name']);
    }

    const options = combination.get('options');
    const optionIndex = result.options.findIndex(findSameVariantCombination);

    if (optionIndex === -1) {
      result.indexes = result.indexes.push(combinationIndex);
      result.options = result.options.push(combination.set('options', options.slice(variantIndex, variantIndex + 1)));
    } else if (combination.get(ATTRIBUTE.VISIBILITY) && !result.options.getIn([optionIndex, ATTRIBUTE.VISIBILITY])) {
      result.options = result.options.setIn([optionIndex, ATTRIBUTE.VISIBILITY], combination.get(ATTRIBUTE.VISIBILITY));
    }

    return result;
  }

  return combinations.reduce(reduceCombinationsToOptionsAndIndexes, { options: EMPTY.LIST, indexes: EMPTY.LIST });
}
